import React from 'react';
import moment from 'moment';
import { useSelector } from 'react-redux';

import { T, NUMERIC } from '../customer/customers.theme';
import {
  CalendarIcon,
  SearchIcon,
  Spinner,
} from '../customer/CustomersUI';
import { SearchField } from '../menu/MenuUI';
import DateRangePopover from '../orders/DateRangePopover';
import { DownloadIcon } from '../orders/OrdersIcons';
import { money, percent } from '../expenses/expenses.summary';
import { fetchOrdersForRange } from './reportsApi';
import {
  SORT,
  filterRows,
  needsReconciliation,
  shareOf,
  sortRows,
  summariseSales,
} from './sales.summary';
import exportSalesPdf from './exportSalesPdf';

/**
 * Sales summary — what was sold, item by item.
 *
 * The phone's `oders_summary_screen.dart` brought across, including the two
 * things that make it read correctly:
 *
 *   • the sort toggle, and the share column that follows it. Sorting by money
 *     while showing a share of units puts the longest bar on a row that is not
 *     at the top, which reads as a bug.
 *
 *   • the reconciliation at the foot. The item lines add up to more than the
 *     takings whenever a discount was given, and rather than quietly bending
 *     one figure to match the other, both are shown with the gap named.
 *
 * The figures are derived here, from the orders list, because the server has
 * no sales-summary endpoint — the phone derives them the same way from the
 * same endpoint, so the two clients cannot disagree.
 */

const FMT = 'YYYY-MM-DD';

const COPY = {
  title: 'Sales summary',
  breakdown: 'Breakdown',
  quantity: 'Quantity',
  revenue: 'Revenue',
  sortedBy: 'Sorted by',

  products: 'Products',
  itemsSold: 'Items sold',
  totalSales: 'Total sales',
  discount: 'Discount',

  rank: '#',
  product: 'Product',
  share: 'Share',
  sales: 'Sales',
  units: 'units',

  subtotal: 'Subtotal',
  adjustment: 'Adjustment',
  netTakings: 'Net takings',

  search: 'Search an item',
  exportPdf: 'Export PDF',

  emptyTitle: 'No sales in this period',
  emptyBody: 'Nothing was sold in this date range. Try another period.',
  noMatch: (query) => `Nothing called "${query}".`,

  errorTitle: 'Could not load the sales',
  retry: 'Try again',

  orders: (n) => (n === 1 ? '1 order' : `${n} orders`),
};

/** Today, which is the period this report is read for most of the time. */
const todayRange = () => ({ start: moment().format(FMT), end: moment().format(FMT) });

const fmtQuantity = (value) =>
  Number.isInteger(Number(value)) ? String(Number(value)) : Number(value).toFixed(2);

export default function SalesSummary() {
  const { business } = useSelector((state) => state.login.activeUser);
  const token = useSelector((state) => state.login.token);

  const [range, setRange] = React.useState(todayRange);
  const [orders, setOrders] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [datesOpen, setDatesOpen] = React.useState(false);
  const [attempt, setAttempt] = React.useState(0);

  const [sort, setSort] = React.useState(SORT.QUANTITY);
  const [query, setQuery] = React.useState('');

  const dateAnchor = React.useRef(null);

  React.useEffect(() => {
    if (!business?._id) return undefined;

    let cancelled = false;
    setLoading(true);
    setError('');

    fetchOrdersForRange({ businessId: business._id, start: range.start, end: range.end, token })
      .then((rows) => {
        if (cancelled) return;
        setOrders(rows);
      })
      .catch((err) => {
        if (cancelled) return;
        setOrders([]);
        setError(err?.response?.data?.message || COPY.errorTitle);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [business, token, range, attempt]);

  const summary = React.useMemo(() => summariseSales(orders), [orders]);

  /** Sorted first, then filtered, so the numbering follows the whole report. */
  const rows = React.useMemo(() => sortRows(summary.rows, sort), [summary.rows, sort]);
  const visible = React.useMemo(() => filterRows(rows, query), [rows, query]);

  const rangeLabel = React.useMemo(
    () =>
      `${moment(range.start, FMT).format('D MMM YYYY')} — ${moment(range.end, FMT).format(
        'D MMM YYYY'
      )}`,
    [range]
  );

  const currency = business?.currencyMark || '$';
  const sortLabel = sort === SORT.REVENUE ? COPY.revenue : COPY.quantity;

  const exportPdf = () => {
    exportSalesPdf({
      // What the sheet shows is what the screen shows, search included.
      rows: visible,
      summary,
      business,
      range,
      sortLabel,
    });
  };

  const empty = !loading && !error && !summary.rows.length;

  return (
    <div style={{ maxWidth: 940, display: 'flex', flexDirection: 'column', gap: 18 }}>
      {/* ── the four figures, and the period they belong to ── */}
      <div
        style={{
          background: T.surface,
          border: `1px solid ${T.border}`,
          borderRadius: T.radiusPanel,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            flexWrap: 'wrap',
            padding: '16px 18px 14px',
          }}
        >
          <span style={{ fontSize: 16, fontWeight: 700, flex: 1 }}>{COPY.title}</span>

          <div style={{ position: 'relative' }}>
            <button
              type="button"
              ref={dateAnchor}
              onClick={() => setDatesOpen((open) => !open)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                height: 38,
                padding: '0 14px',
                border: `1px solid ${T.border}`,
                borderRadius: T.radiusPill,
                background: T.surface,
                font: 'inherit',
                fontSize: 13,
                fontWeight: 600,
                color: T.ink,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              <CalendarIcon size={15} />
              {rangeLabel}
            </button>

            {datesOpen ? (
              <div style={{ position: 'absolute', top: '100%', right: 0, width: 318, height: 0 }}>
                <DateRangePopover
                  range={range}
                  anchorRef={dateAnchor}
                  onClose={() => setDatesOpen(false)}
                  onApply={(next) => {
                    setRange(next);
                    setDatesOpen(false);
                  }}
                />
              </div>
            ) : null}
          </div>

          <button
            type="button"
            onClick={exportPdf}
            disabled={loading || !visible.length}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 7,
              height: 38,
              padding: '0 14px',
              border: `1px solid ${T.borderStrong}`,
              borderRadius: T.radiusPill,
              background: T.surface,
              font: 'inherit',
              fontSize: 13,
              fontWeight: 600,
              color: T.ink,
              opacity: loading || !visible.length ? 0.45 : 1,
              cursor: loading || !visible.length ? 'not-allowed' : 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            <DownloadIcon size={15} color={T.ink} />
            {COPY.exportPdf}
          </button>
        </div>

        <div style={{ display: 'flex', borderTop: `1px solid ${T.hairline}`, flexWrap: 'wrap' }}>
          <Figure label={COPY.products} value={summary.productCount} loading={loading} />
          <Figure label={COPY.itemsSold} value={fmtQuantity(summary.unitsSold)} loading={loading} />
          <Figure
            label={COPY.totalSales}
            value={money(summary.sales, currency)}
            loading={loading}
          />
          <Figure
            label={COPY.discount}
            // Negated, not prefixed: money() prints its own minus sign.
            value={money(-summary.discount, currency)}
            colour={summary.discount > 0 ? T.danger : T.ink}
            loading={loading}
            last
          />
        </div>
      </div>

      {error ? (
        <Message title={COPY.errorTitle} body={error} onRetry={() => setAttempt((n) => n + 1)} />
      ) : empty ? (
        <Message title={COPY.emptyTitle} body={COPY.emptyBody} />
      ) : (
        <div
          style={{
            background: T.surface,
            border: `1px solid ${T.border}`,
            borderRadius: T.radiusPanel,
            overflow: 'hidden',
          }}
        >
          {/* ── what the table is ordered by, and a way to find one item ── */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              flexWrap: 'wrap',
              padding: '12px 16px',
              borderBottom: `1px solid ${T.hairline}`,
            }}
          >
            <span style={{ fontSize: 13.5, fontWeight: 700 }}>{COPY.breakdown}</span>
            <span style={{ fontSize: 12, color: T.inkFaint }}>
              {COPY.orders(summary.orderCount)}
            </span>

            <div style={{ display: 'flex', gap: 3, padding: 3, background: T.shade, borderRadius: 8 }}>
              {[
                { id: SORT.QUANTITY, label: COPY.quantity },
                { id: SORT.REVENUE, label: COPY.revenue },
              ].map((entry) => {
                const active = sort === entry.id;
                return (
                  <button
                    key={entry.id}
                    type="button"
                    onClick={() => setSort(entry.id)}
                    style={{
                      height: 28,
                      padding: '0 12px',
                      border: 'none',
                      borderRadius: 6,
                      background: active ? T.surface : 'transparent',
                      boxShadow: active ? '0 1px 2px rgba(21,24,29,0.10)' : 'none',
                      font: 'inherit',
                      fontSize: 12,
                      fontWeight: 600,
                      color: active ? T.ink : T.inkSoft,
                      cursor: 'pointer',
                    }}
                  >
                    {entry.label}
                  </button>
                );
              })}
            </div>

            <div style={{ marginLeft: 'auto', width: 240 }}>
              <SearchField
                value={query}
                onChange={setQuery}
                placeholder={COPY.search}
                icon={<SearchIcon size={15} />}
              />
            </div>
          </div>

          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 40, color: T.inkFaint }}>
              <Spinner size={20} />
            </div>
          ) : !visible.length ? (
            <div style={{ padding: '40px 20px', textAlign: 'center', fontSize: 13.5, color: T.inkSoft }}>
              {COPY.noMatch(query.trim())}
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5 }}>
              <thead>
                <tr>
                  {[
                    { label: COPY.rank, align: 'left', width: 44 },
                    { label: COPY.product, align: 'left' },
                    { label: COPY.quantity, align: 'right', width: 120 },
                    { label: COPY.share, align: 'right', width: 90 },
                    { label: COPY.sales, align: 'right', width: 120 },
                  ].map((column) => (
                    <th
                      key={column.label}
                      style={{
                        width: column.width,
                        padding: '10px 16px 8px',
                        textAlign: column.align,
                        borderBottom: `1px solid ${T.border}`,
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: 0.4,
                        textTransform: 'uppercase',
                        color: T.inkFaint,
                      }}
                    >
                      {column.label}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {visible.map((row, position) => {
                  const share = shareOf(row, summary, sort);

                  return (
                    <tr key={row.name}>
                      <td
                        style={{
                          padding: '9px 16px',
                          borderBottom: `1px solid ${T.hairline}`,
                          color: T.inkFaint,
                          ...NUMERIC,
                        }}
                      >
                        {position + 1}
                      </td>

                      <td
                        style={{
                          padding: '9px 16px',
                          borderBottom: `1px solid ${T.hairline}`,
                          fontWeight: 600,
                        }}
                      >
                        {row.name}
                      </td>

                      <td
                        style={{
                          padding: '9px 16px',
                          borderBottom: `1px solid ${T.hairline}`,
                          textAlign: 'right',
                          ...NUMERIC,
                        }}
                      >
                        {fmtQuantity(row.quantity)}{' '}
                        <span style={{ fontSize: 11.5, color: T.inkFaint }}>{COPY.units}</span>
                      </td>

                      {/* The bar is the share, drawn behind the figure so a
                          long table can be read by shape before it is read by
                          number. */}
                      <td
                        style={{
                          padding: '9px 16px',
                          borderBottom: `1px solid ${T.hairline}`,
                          textAlign: 'right',
                          color: T.inkSoft,
                          ...NUMERIC,
                        }}
                      >
                        <span
                          style={{
                            display: 'inline-block',
                            width: 46,
                            height: 4,
                            marginRight: 8,
                            borderRadius: T.radiusPill,
                            background: T.shade,
                            verticalAlign: 'middle',
                          }}
                        >
                          <span
                            style={{
                              display: 'block',
                              width: `${Math.max(0, Math.min(100, share))}%`,
                              height: '100%',
                              borderRadius: T.radiusPill,
                              background: T.brand,
                            }}
                          />
                        </span>
                        {percent(share)}
                      </td>

                      <td
                        style={{
                          padding: '9px 16px',
                          borderBottom: `1px solid ${T.hairline}`,
                          textAlign: 'right',
                          fontWeight: 700,
                          ...NUMERIC,
                        }}
                      >
                        {money(row.sales, currency)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>

              <tfoot>
                <tr>
                  <td colSpan={2} style={{ padding: '12px 16px', fontWeight: 700 }}>
                    {COPY.subtotal}
                  </td>
                  <td style={{ padding: '12px 16px', textAlign: 'right', ...NUMERIC }}>
                    {fmtQuantity(summary.unitsSold)}
                  </td>
                  <td />
                  <td
                    style={{
                      padding: '12px 16px',
                      textAlign: 'right',
                      fontWeight: 700,
                      ...NUMERIC,
                    }}
                  >
                    {money(summary.sales, currency)}
                  </td>
                </tr>

                {/* Only when the item lines and the takings actually differ —
                    which is exactly when a discount was given. */}
                {needsReconciliation(summary) ? (
                  <React.Fragment>
                    <tr>
                      <td colSpan={4} style={{ padding: '0 16px 6px', color: T.inkSoft }}>
                        {summary.gap >= 0 ? COPY.discount : COPY.adjustment}
                      </td>
                      <td
                        style={{
                          padding: '0 16px 6px',
                          textAlign: 'right',
                          color: T.danger,
                          ...NUMERIC,
                        }}
                      >
                        {money(-summary.gap, currency)}
                      </td>
                    </tr>
                    <tr>
                      <td
                        colSpan={4}
                        style={{
                          padding: '10px 16px 14px',
                          borderTop: `1px solid ${T.border}`,
                          fontWeight: 700,
                        }}
                      >
                        {COPY.netTakings}
                      </td>
                      <td
                        style={{
                          padding: '10px 16px 14px',
                          borderTop: `1px solid ${T.border}`,
                          textAlign: 'right',
                          fontWeight: 700,
                          ...NUMERIC,
                        }}
                      >
                        {money(summary.net, currency)}
                      </td>
                    </tr>
                  </React.Fragment>
                ) : null}
              </tfoot>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

/* ── the small pieces ─────────────────────────────────────────────────── */

function Figure({ label, value, colour, loading, last }) {
  return (
    <div
      style={{
        flex: 1,
        minWidth: 150,
        padding: '12px 18px',
        borderRight: last ? 'none' : `1px solid ${T.hairline}`,
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: 0.4,
          textTransform: 'uppercase',
          color: T.inkFaint,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 20,
          fontWeight: 700,
          marginTop: 4,
          color: loading ? T.inkGhost : colour || T.ink,
          ...NUMERIC,
        }}
      >
        {loading ? '—' : value}
      </div>
    </div>
  );
}

function Message({ title, body, onRetry }) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 10,
        padding: '60px 20px',
        background: T.surface,
        border: `1px solid ${T.border}`,
        borderRadius: T.radiusPanel,
        textAlign: 'center',
      }}
    >
      <span style={{ fontSize: 15, fontWeight: 600 }}>{title}</span>
      {body ? <span style={{ fontSize: 13, color: T.inkSoft }}>{body}</span> : null}
      {onRetry ? (
        <button
          type="button"
          onClick={onRetry}
          style={{
            height: 36,
            padding: '0 16px',
            border: `1px solid ${T.borderStrong}`,
            borderRadius: T.radiusPill,
            background: T.surface,
            font: 'inherit',
            fontSize: 13,
            fontWeight: 600,
            color: T.ink,
            cursor: 'pointer',
          }}
        >
          {COPY.retry}
        </button>
      ) : null}
    </div>
  );
}
